/**
 * SF-4758: add or edit one priority item. Same limits as POST/PATCH /items on sf-api; an
 * emptied description is sent as null, which clears it.
 *
 * SF-4794: when adding, a Jira epic can be picked to fill in the fields; the item then carries
 * the epic's key. The fields stay editable and keep the key, unless the Jira link stops being
 * the picked epic's link (cleared or changed), since the key would then point elsewhere.
 *
 * SF-5080: a list select (adding puts the item in that list; editing can move it), any Jira
 * ticket can be picked, and the window also accepts a suggestion or turns Eva's proposal into
 * an item, filled in from them (`prefill`) and still editable.
 *
 * SF-5087: an item holds up to 10 Jira tickets, picked one by one (adding or editing) and
 * listed as removable links; the first pick of a new item fills in its title and description.
 * The companies that want it are picked the same way as on the Suggest form.
 */
import { useEffect, useId, useState } from 'react';
import { Alert, Form, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import { COMPANIES_MAX, ITEM_DESCRIPTION_MAX, ITEM_TITLE_MAX, JIRA_KEYS_MAX } from '../constants';
import {
  type ItemInput,
  type JiraEpic,
  type JiraLink,
  type PriorityCompany,
  type PriorityItem,
  type PriorityList,
} from '../api/prioritiesClient';
import { JiraEpicPicker } from './JiraEpicPicker';
import { CompanySelect } from './CompanySelect';

// Checks run on the value that is sent: the submit trims every field.
const trim = (value: unknown) => (typeof value === 'string' ? value.trim() : value);

interface Props {
  open: boolean;
  /** The item being edited; none when adding. */
  item: PriorityItem | null;
  onSubmit: (values: ItemInput) => Promise<void>;
  onClose: () => void;
  /** The list the item goes in by default: the one the page shows. */
  listId: string;
  /** Lists to choose from; the select is left out when there is only one. */
  lists?: PriorityList[];
  /** Fills in a new item, as when accepting a suggestion. */
  prefill?: {
    title?: string;
    description?: string | null;
    listId?: string | null;
    companies?: PriorityCompany[];
  };
  heading?: string;
  okText?: string;
  /** Shown above the form, as the suggestions an Eva proposal covers. */
  extra?: React.ReactNode;
  /** False turns saving off, as while what the window decides on may be out of date. */
  canSubmit?: boolean;
}

interface FormValues {
  title: string;
  description?: string;
  companyIds?: string[];
  listId?: string;
}

export const ItemFormModal: React.FC<Props> = ({
  open,
  item,
  onSubmit,
  onClose,
  listId,
  lists = [],
  prefill,
  heading,
  okText,
  extra,
  canSubmit = true,
}) => {
  const [form] = Form.useForm<FormValues>();
  // Several item windows can be on the page (add, accept, Eva); a name per window keeps each
  // field's id, and so its label, its own.
  const formName = `item-form-${useId().replace(/:/g, '')}`;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The picked Jira tickets, in the order picked.
  const [jira, setJira] = useState<JiraLink[]>([]);
  const companies = item?.companies ?? prefill?.companies ?? [];

  useEffect(() => {
    if (!open) return;
    setError(null);
    setJira(item?.jiraLinks ?? []);
    form.setFieldsValue({
      title: item?.title ?? prefill?.title ?? '',
      description: item?.description ?? prefill?.description ?? '',
      companyIds: (item?.companies ?? prefill?.companies ?? []).map((c) => c.id),
      listId: item?.listId ?? prefill?.listId ?? listId,
    });
  }, [open, item, prefill, listId, form]);

  // Archived lists take no new items; the item's own list always stays an option.
  const listOptions = lists
    .filter((l) => l.status !== 'archived' || l.id === item?.listId)
    .map((l) => ({ value: l.id, label: l.name }));
  const startList = item?.listId ?? listId;
  // The picker marks the tickets already open in the list chosen in the form.
  const chosenList: string = Form.useWatch('listId', form) ?? prefill?.listId ?? startList;
  // Over the cap (as an Eva proposal's companies can start), saving waits for removals.
  const companyCount = (Form.useWatch('companyIds', form) ?? []).length;
  const savable = canSubmit && companyCount <= COMPANIES_MAX;

  const pick = (epic: JiraEpic) => {
    if (jira.length >= JIRA_KEYS_MAX || jira.some((j) => j.key === epic.key)) return;
    if (!item && jira.length === 0) {
      form.setFieldsValue({ title: epic.title, description: epic.description ?? '' });
    }
    setJira([...jira, { key: epic.key, url: epic.url }]);
  };

  const submit = async () => {
    if (!savable) return;
    // A failed check is already shown under its field; nothing more to do.
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      await onSubmit({
        title: values.title.trim(),
        description: values.description?.trim() || null,
        jiraKeys: jira.map((j) => j.key),
        companyIds: values.companyIds ?? [],
        // Editing sends the list only when the item moves.
        ...(!item || (values.listId && values.listId !== startList)
          ? { listId: values.listId ?? startList }
          : {}),
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={heading ?? (item ? 'Edit item' : 'Add item')}
      okText={okText ?? (item ? 'Save' : 'Add item')}
      onOk={submit}
      okButtonProps={{ disabled: !savable }}
      confirmLoading={saving}
      onCancel={onClose}
      // Mounted while closed, so the form exists when opening fills it in.
      forceRender
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {/* Unmounted while closed, so a reopened window starts with a fresh search. */}
      {extra}
      {open && (
        <JiraEpicPicker
          onPick={pick}
          listId={chosenList}
          pickedKeys={jira.map((j) => j.key)}
          ownItemId={item?.id}
          full={jira.length >= JIRA_KEYS_MAX}
        />
      )}
      <div style={{ margin: '8px 0 12px' }}>
        <Typography.Text strong>Jira tickets</Typography.Text>{' '}
        <Typography.Text type="secondary">
          ({jira.length} of {JIRA_KEYS_MAX}. The item closes when all are done.)
        </Typography.Text>
        <div>
          {jira.length === 0 ? (
            <Typography.Text type="secondary">None picked</Typography.Text>
          ) : (
            <Space size={[4, 4]} wrap>
              {jira.map((j) => (
                <Tag
                  key={j.key}
                  closable
                  closeIcon={<span aria-label={`Remove ${j.key}`}>×</span>}
                  onClose={(e) => {
                    e.preventDefault();
                    setJira(jira.filter((x) => x.key !== j.key));
                  }}
                >
                  <a href={j.url} target="_blank" rel="noreferrer">
                    {j.key}
                  </a>
                </Tag>
              ))}
            </Space>
          )}
        </div>
      </div>
      <Form form={form} name={formName} layout="vertical">
        {listOptions.length > 1 ? (
          <Form.Item name="listId" label="List">
            <Select options={listOptions} />
          </Form.Item>
        ) : (
          <Form.Item name="listId" hidden>
            <Input />
          </Form.Item>
        )}
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, whitespace: true, message: 'Give the item a title' },
            {
              max: ITEM_TITLE_MAX,
              transform: trim,
              message: `At most ${ITEM_TITLE_MAX} characters`,
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            {
              max: ITEM_DESCRIPTION_MAX,
              transform: trim,
              message: `At most ${ITEM_DESCRIPTION_MAX} characters`,
            },
          ]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="companyIds" label="Companies that want this">
          <CompanySelect initial={companies} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
